import {
  Box,
  Drawer,
  Typography,
  IconButton,
  Button,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Slider,
  Radio,
  RadioGroup,
  FormControl,
  FormLabel,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

const rubikfont = {
  fontFamily: "Rubik, sans-serif",
};

const Filter = ({
  open,
  onClose,
  priceRange,
  setPriceRange,
  selectedBrands,
  setSelectedBrands,
  selectedIdealFor,
  setSelectedIdealFor,
  selectedColors,
  setSelectedColors,
  brands,
  colors,
  onClearFilters,
}) => {
  const handleBrandChange = (brand) => {
    setSelectedBrands((prev) =>
      prev.includes(brand) ? prev.filter((b) => b !== brand) : [...prev, brand]
    );
  };

  const handleColorChange = (color) => {
    setSelectedColors((prev) =>
      prev.includes(color) ? prev.filter((c) => c !== color) : [...prev, color]
    );
  };

  const handlePriceChange = (event, newValue) => {
    setPriceRange(newValue);
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{
        "& .MuiDrawer-paper": {
          width: { xs: "100%", sm: 420 },
          maxWidth: "100vw",
          boxSizing: "border-box",
          boxShadow: "0 0 24px rgba(0,0,0,0.1)",
          border: "none",
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          p: 3,
          borderBottom: "1px solid",
          borderColor: "divider",
          position: "sticky",
          top: 0,
          backgroundColor: "background.paper",
          zIndex: 1,
        }}
      >
        <Typography
          variant="h5"
          sx={{ ...rubikfont, fontWeight: 700, color: "text.primary" }}
        >
          Filters
        </Typography>
        <IconButton
          onClick={onClose}
          sx={{
            backgroundColor: "action.hover",
            "&:hover": {
              backgroundColor: "action.selected",
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Scrollable Content */}
      <Box
        sx={{
          p: 3,
          overflowY: "auto",
          height: "calc(100vh - 140px)",
          "&::-webkit-scrollbar": {
            width: 4,
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "divider",
            borderRadius: 2,
          },
        }}
      >
        {/* Price Filter */}
        <Box
          sx={{
            mb: 4,
            p: 2.5,
            borderRadius: 2,
            backgroundColor: "background.default",
          }}
        >
          <FormLabel
            component="legend"
            sx={{
              ...rubikfont,
              fontWeight: 600,
              mb: 2.5,
              display: "block",
              fontSize: "1rem",
              color: "text.primary",
            }}
          >
            Price Range
          </FormLabel>
          <Slider
            value={priceRange}
            onChange={handlePriceChange}
            valueLabelDisplay="auto"
            valueLabelFormat={(value) => `₹${value}`}
            min={0}
            max={5000}
            sx={{
              mb: 2,
              color: "#222",
              "& .MuiSlider-thumb": {
                width: 16,
                height: 16,
              },
            }}
          />
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                backgroundColor: "background.paper",
                px: 2,
                py: 1,
                borderRadius: 1,
                border: "1px solid",
                borderColor: "divider",
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 500, color: "text.primary" }}>
                ₹{priceRange[0]}
              </Typography>
            </Box>
            <Box sx={{ width: 8, height: 1, backgroundColor: "divider", mx: 1 }} />
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                backgroundColor: "background.paper",
                px: 2,
                py: 1,
                borderRadius: 1,
                border: "1px solid",
                borderColor: "divider",
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 500, color: "text.primary" }}>
                ₹{priceRange[1]}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Brand Filter */}
        <Box
          sx={{
            mb: 4,
            p: 2.5,
            borderRadius: 2,
            backgroundColor: "background.default",
          }}
        >
          <FormLabel
            component="legend"
            sx={{
              ...rubikfont,
              fontWeight: 600,
              mb: 2,
              display: "block",
              fontSize: "1rem",
              color: "text.primary",
            }}
          >
            Brand
          </FormLabel>
          <FormGroup sx={{ gap: 0.5 }}>
            {brands.map((brand) => (
              <FormControlLabel
                key={brand}
                control={
                  <Checkbox
                    checked={selectedBrands.includes(brand)}
                    onChange={() => handleBrandChange(brand)}
                    sx={{
                      color: "action.active",
                      "&.Mui-checked": {
                        color: "#222",
                      },
                      padding: "8px",
                    }}
                  />
                }
                label={
                  <Typography sx={{ ...rubikfont, fontSize: "0.95rem" }}>
                    {brand}
                  </Typography>
                }
                sx={{
                  margin: 0,
                  padding: "6px 8px",
                  borderRadius: 1,
                  "&:hover": {
                    backgroundColor: "action.hover",
                  },
                }}
              />
            ))}
          </FormGroup>
        </Box>

        {/* Ideal For Filter */}
        <Box
          sx={{
            mb: 4,
            p: 2.5,
            borderRadius: 2,
            backgroundColor: "background.default",
          }}
        >
          <FormLabel
            component="legend"
            sx={{
              ...rubikfont,
              fontWeight: 600,
              mb: 2,
              display: "block",
              fontSize: "1rem",
              color: "text.primary",
            }}
          >
            Ideal For
          </FormLabel>
          <FormControl component="fieldset" sx={{ width: "100%" }}>
            <RadioGroup
              value={selectedIdealFor}
              onChange={(e) => setSelectedIdealFor(e.target.value)}
              sx={{ gap: 0.5 }}
            >
              {["all", "men", "unisex"].map((option) => (
                <FormControlLabel
                  key={option}
                  value={option}
                  control={
                    <Radio
                      sx={{
                        color: "action.active",
                        "&.Mui-checked": {
                          color: "#222",
                        },
                        padding: "8px",
                      }}
                    />
                  }
                  label={
                    <Typography
                      sx={{
                        ...rubikfont,
                        fontSize: "0.95rem",
                        textTransform: "capitalize",
                      }}
                    >
                      {option === "all" ? "All" : option}
                    </Typography>
                  }
                  sx={{
                    margin: 0,
                    padding: "6px 8px",
                    borderRadius: 1,
                    "&:hover": {
                      backgroundColor: "action.hover",
                    },
                  }}
                />
              ))}
            </RadioGroup>
          </FormControl>
        </Box>

        {/* Color Filter */}
        <Box
          sx={{
            mb: 4,
            p: 2.5,
            borderRadius: 2,
            backgroundColor: "background.default",
          }}
        >
          <FormLabel
            component="legend"
            sx={{
              ...rubikfont,
              fontWeight: 600,
              mb: 2,
              display: "block",
              fontSize: "1rem",
              color: "text.primary",
            }}
          >
            Color
          </FormLabel>
          <FormGroup sx={{ gap: 0.5 }}>
            {colors.map((color) => (
              <FormControlLabel
                key={color}
                control={
                  <Checkbox
                    checked={selectedColors.includes(color)}
                    onChange={() => handleColorChange(color)}
                    sx={{
                      color: "action.active",
                      "&.Mui-checked": {
                        color: "#222",
                      },
                      padding: "8px",
                    }}
                  />
                }
                label={
                  <Typography
                    sx={{
                      ...rubikfont,
                      fontSize: "0.95rem",
                      textTransform: "capitalize",
                    }}
                  >
                    {color}
                  </Typography>
                }
                sx={{
                  margin: 0,
                  padding: "6px 8px",
                  borderRadius: 1,
                  "&:hover": {
                    backgroundColor: "action.hover",
                  },
                }}
              />
            ))}
          </FormGroup>
        </Box>
      </Box>

      {/* Sticky Action Buttons */}
      <Box
        sx={{
          p: 3,
          borderTop: "1px solid",
          borderColor: "divider",
          backgroundColor: "background.paper",
          position: "sticky",
          bottom: 0,
        }}
      >
        <Box sx={{ display: "flex", gap: 2 }}>
          <Button
            variant="outlined"
            fullWidth
            onClick={onClearFilters}
            sx={{
              ...rubikfont,
              py: 1.5,
              borderColor: "divider",
              color: "text.primary",
              fontWeight: 500,
              "&:hover": {
                borderColor: "text.primary",
                backgroundColor: "transparent",
              },
            }}
          >
            Clear All
          </Button>
          <Button
            variant="contained"
            fullWidth
            onClick={onClose}
            sx={{
              ...rubikfont,
              py: 1.5,
              backgroundColor: "#222",
              fontWeight: 600,
              fontSize: "0.95rem",
              "&:hover": {
                backgroundColor: "#444",
                transform: "translateY(-1px)",
                boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
              },
              "&:active": {
                transform: "translateY(0)",
              },
              transition: "all 0.2s ease",
            }}
          >
            Apply Filters
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};

export default Filter;